/**
 * Lead Coverage Area picker.
 *
 * Rendered on the Become Ox Approved page so an installer can tell us
 * where they'll travel to for work. Multi-region + multi-territory:
 *   – Tick a whole region → all its territories tick on
 *   – Tick individual territories → the parent goes indeterminate
 *   – Save writes to PUT /api/installer/lead-areas
 *
 * The setting is shared across all 3 brands (one coverage list per
 * installer) so ticking it here is enough — no per-brand duplication.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapPin, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';

export default function LeadCoverageCard({ theme }) {
  const [catalogue, setCatalogue] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [initialKey, setInitialKey] = useState('');
  const [openRegion, setOpenRegion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/installer/lead-areas`, { credentials: 'include' });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Failed to load');
      const data = await res.json();
      setCatalogue(data.catalogue || []);
      const s = new Set(data.selected_territories || []);
      setSelected(s);
      setInitialKey([...s].sort().join('|'));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleTerritory = (name) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const toggleRegion = (region, terrs) => {
    setSelected(prev => {
      const next = new Set(prev);
      const allOn = terrs.every(t => next.has(t));
      if (allOn) {
        for (const t of terrs) next.delete(t);
      } else {
        for (const t of terrs) next.add(t);
      }
      return next;
    });
  };

  const clearAll = () => setSelected(new Set());

  const currentKey = useMemo(() => [...selected].sort().join('|'), [selected]);
  const dirty = currentKey !== initialKey;

  const summary = useMemo(() => {
    if (selected.size === 0) return 'No areas selected yet';
    const regionsCovered = catalogue
      .filter(({ territories }) => territories.some(t => selected.has(t)))
      .map(r => r.region);
    return `${selected.size} territor${selected.size === 1 ? 'y' : 'ies'} across ${regionsCovered.length} region${regionsCovered.length === 1 ? '' : 's'}`;
  }, [selected, catalogue]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/installer/lead-areas`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ territories: [...selected] }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Save failed');
      const data = await res.json();
      const fresh = new Set(data.selected_territories || []);
      setSelected(fresh);
      setInitialKey([...fresh].sort().join('|'));
      toast.success('Coverage saved.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-4 sm:p-5" data-testid="lead-coverage-card">
      <div className="flex flex-wrap items-start gap-3 justify-between mb-3">
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
            style={{ background: `${theme.buttonBg}1A`, color: theme.buttonBg }}
          >
            <MapPin className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-base text-gray-900">Lead Coverage Area</h3>
            <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
              Tick the territories you'll travel to for work — whole region or a few patches within it.
              Not exclusivity, just so we know where to send leads.
            </p>
            <p className="text-[11px] text-gray-500 mt-1" data-testid="coverage-summary">{summary}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {selected.size > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearAll}
              disabled={saving}
              data-testid="coverage-clear"
            >
              Clear all
            </Button>
          )}
          <Button
            onClick={save}
            disabled={saving || !dirty}
            data-testid="coverage-save"
            className="font-semibold"
            style={{ background: theme.buttonBg, color: theme.buttonText }}
          >
            {saving ? 'Saving…' : dirty ? `Save (${selected.size})` : 'Saved'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-6 text-gray-500 text-sm">Loading regions…</div>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {catalogue.map(({ region, territories }) => {
            const on = territories.filter(t => selected.has(t)).length;
            const total = territories.length;
            const state = on === 0 ? 'none' : on === total ? 'all' : 'some';
            const isOpen = openRegion === region;
            return (
              <div key={region}>
                <div
                  className={`flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 cursor-pointer ${state === 'all' ? 'bg-emerald-50/40' : state === 'some' ? 'bg-amber-50/30' : ''}`}
                >
                  {/* Region checkbox — tri-state visual */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleRegion(region, territories); }}
                    data-testid={`coverage-region-toggle-${region}`}
                    aria-checked={state === 'all' ? 'true' : state === 'some' ? 'mixed' : 'false'}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                      state === 'all' ? 'border-emerald-600 bg-emerald-600 text-white' :
                      state === 'some' ? 'border-amber-500 bg-amber-500 text-white' :
                      'border-gray-300 bg-white hover:border-gray-400'
                    }`}
                  >
                    {state === 'all' && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                    {state === 'some' && <div className="w-2.5 h-0.5 bg-white rounded" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpenRegion(isOpen ? null : region)}
                    data-testid={`coverage-region-expand-${region}`}
                    className="flex-1 flex items-center justify-between text-left min-w-0"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-gray-900 truncate">{region}</div>
                      <div className="text-[11px] text-gray-500">
                        {on === 0 ? `${total} territories` : `${on} of ${total} selected`}
                      </div>
                    </div>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </button>
                </div>
                {isOpen && (
                  <div className="bg-white grid grid-cols-1 sm:grid-cols-2 gap-x-3 px-3 py-2 border-t border-gray-100">
                    {territories.map(t => {
                      const checked = selected.has(t);
                      return (
                        <label
                          key={t}
                          className="flex items-center gap-2 py-1.5 text-sm cursor-pointer hover:bg-gray-50 rounded px-1"
                          data-testid={`coverage-territory-row-${t}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleTerritory(t)}
                            data-testid={`coverage-territory-${t}`}
                            className="w-4 h-4 accent-emerald-600 cursor-pointer"
                          />
                          <span className={checked ? 'text-gray-900 font-medium' : 'text-gray-700'}>{t}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
